<template>
  <div
    v-bind="forwardedAttrs()"
    class="sp-list-item"
    :class="modifiers"
    :data-value="value"
    :aria-disabled="ariaDisabled"
  >
    <sp-divider class="sp-list-item__divider"></sp-divider>
    <div class="sp-list-item__prepend">
      <slot name="prepend">
        <span v-if="showCheckbox" class="sp-list-item__checkbox" aria-hidden="true">
          <sp-icon name="checkbox-check"></sp-icon>
        </span>
        <sp-avatar
          v-else-if="prependAvatarProps"
          v-bind="prependAvatarProps"
          class="sp-list-item__prepend-avatar"
        ></sp-avatar>
        <span
          v-else-if="prependBackground"
          class="sp-list-item__prepend-decoration"
          :class="prependBackgroundClass"
          :style="prependBackgroundStyle"
        >
          <sp-icon v-bind="prependIconProps" :name="prependIconName" class="sp-list-item__prepend-icon"></sp-icon>
        </span>
        <sp-icon
          v-else-if="prependIconName"
          v-bind="prependIconProps"
          :name="prependIconName"
          class="sp-list-item__prepend-icon"
        ></sp-icon>
        <sp-icon
          v-else-if="prependIconUrl"
          :url="prependIconUrl"
          class="sp-list-item__prepend-icon sp-list-item__prepend-icon--url"
        ></sp-icon>
      </slot>
    </div>
    <div class="sp-list-item__content">
      <div class="sp-list-item__text">
        <div class="sp-list-item__title">{{ title }}</div>
        <div v-if="subtitle" class="sp-list-item__subtitle">
          {{ subtitle }}
        </div>
        <div v-if="tagList.length" class="sp-list-item__tags">
          <span v-for="(tag, index) in tagList" :key="`${index}-${tag}`" class="sp-list-item__tag">{{ tag }}</span>
        </div>
      </div>
    </div>
    <div class="sp-list-item__append" :class="appendClass">
      <slot name="append">
        <sp-progress v-if="loading" type="ellipsis" size="xxx-small"></sp-progress>
        <ListItemAppendCaret
          v-else-if="appendCaret"
          :interactive="appendCaretInteractive"
          :show-disclosure-icon="showDisclosureIcon"
          :disclosure-icon-name="disclosureIconName"
          :expanded="appendCaretExpanded"
          :toggle="appendCaretToggle"
          :label="appendToggleLabel"
          :test-id="props.appendCaretTestId"
          @append-action="emit('append-action')"
        />
        <sp-icon v-else-if="props.appendIcon" :name="props.appendIcon" class="sp-list-item__append-icon"></sp-icon>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, useAttrs } from "vue";
import { forwardableAttrs } from "#ce/utils/forwarded-attrs";
import { toBoolean } from "#ce/utils/props";
import { listItem as listItemProps } from "./props";

import ListItemAppendCaret from "./components/ListItemAppendCaret.ce.vue";

defineOptions({ inheritAttrs: false });

// The row's identity and state belong to the host, which `sp-list` sets them on. Repeated here they
// announce a second row nested inside the row.
const HOST_OWNED_ATTRIBUTES = ["role", "id", "tabindex", "aria-selected"];

const attrs = useAttrs();

function forwardedAttrs() {
  const forwarded = forwardableAttrs(attrs);

  return Object.fromEntries(Object.entries(forwarded).filter(([name]) => !HOST_OWNED_ATTRIBUTES.includes(name)));
}

const emit = defineEmits(["append-action"]);

const props = defineProps(listItemProps);

const isDisabled = computed(() => toBoolean(props.disabled));

// Emit the attribute only when disabled so an enabled row carries no
// `aria-disabled` at all (rather than `aria-disabled="false"`).
const ariaDisabled = computed(() => (isDisabled.value ? "true" : undefined));

const appendClass = computed(() => ({ "--loading": toBoolean(props.loading) }));

const markAsActive = computed(() => toBoolean(props.active) && !toBoolean(props.ignoreActiveState));

// Resolve the active-state override first, preserving the original semantics:
// when the row is active, use `activePrependIcon` as-is (so an unset active icon
// blanks the prepend, unchanged from before). `activePrependIcon` is always a
// plain icon name (String); the base `prependIcon` may be a String name or a
// structured descriptor `{ name, color, background }`.
const resolvedPrependIcon = computed(() => (markAsActive.value ? props.activePrependIcon : props.prependIcon));

// A descriptor is a plain object carrying a `name` (plus optional color /
// background). Arrays and objects without a name are not descriptors — guarding
// here keeps an unexpected object type from silently suppressing the icon.
function isIconDescriptor(icon) {
  return typeof icon === "object" && icon !== null && !Array.isArray(icon) && typeof icon.name === "string";
}

// Normalise `prependAvatar` (a bare image-URL String or a descriptor object)
// into the prop set forwarded to `<sp-avatar>`. Returns null when unset so the
// template falls through to the icon paths.
const prependAvatarProps = computed(() => {
  const avatar = props.prependAvatar;
  if (typeof avatar === "string") {
    return avatar ? { image: avatar } : null;
  }
  if (avatar && typeof avatar === "object" && !Array.isArray(avatar)) {
    return avatar;
  }
  return null;
});

const prependIconName = computed(() => {
  const icon = resolvedPrependIcon.value;
  if (typeof icon === "string") {
    return icon;
  }
  // Descriptor → its name; anything else (object without a name, array, …) → no icon.
  return isIconDescriptor(icon) ? icon.name : undefined;
});

const prependIconColor = computed(() => {
  const icon = resolvedPrependIcon.value;
  return isIconDescriptor(icon) ? icon.color : undefined;
});

const prependBackground = computed(() => {
  const icon = resolvedPrependIcon.value;
  return isIconDescriptor(icon) ? icon.background : undefined;
});

const prependIconProps = computed(() => {
  const iconProps = { ...(props.prependIconProps ?? {}) };

  if (markAsActive.value && props.activePrependIconFillColor) {
    iconProps.fillColor = props.activePrependIconFillColor;
  } else if (prependIconColor.value) {
    iconProps.fillColor = prependIconColor.value;
  }

  return iconProps;
});

// Coerce to an array so a consumer passing `tags: null` (common from JSON) or a
// non-array value never reaches `.length` / `v-for` and throws at render.
const tagList = computed(() => (Array.isArray(props.tags) ? props.tags : []));

// Clamp the background shape to the supported enum (and default to circle) so an
// unexpected value from external data can neither inject extra classes (e.g. a
// string with spaces) nor silently map to no styling.
const BACKGROUND_SHAPES = ["circle", "rounded", "square"];
const prependBackgroundClass = computed(() => {
  const shape = prependBackground.value?.shape;
  return `--prepend-bg-${BACKGROUND_SHAPES.includes(shape) ? shape : "circle"}`;
});

const prependBackgroundStyle = computed(() => {
  const background = prependBackground.value;
  if (!background?.color) {
    return undefined;
  }
  return { backgroundColor: background.color };
});

const loading = computed(() => toBoolean(props.loading));

const appendCaret = computed(() => toBoolean(props.appendCaret));

const appendCaretExpanded = computed(() => toBoolean(props.appendCaretExpanded));

const appendCaretToggle = computed(() => toBoolean(props.appendCaretToggle));

const appendCaretInteractive = computed(() => toBoolean(props.appendCaretInteractive));

// The interactive disclosure toggle must always have an accessible name. Prefer
// the consumer-supplied (translatable) label; fall back to a sensible English
// default so the button is never announced as unlabelled when the label is omitted.
const appendToggleLabel = computed(() => props.appendCaretLabel || "Toggle");

// "disclosure" variant swaps the rotating chevron for a plus / minus toggle
// glyph (matching SpInteractiveCard's expand control and the 2025 redesign).
const showDisclosureIcon = computed(() => appendCaret.value && props.appendCaretVariant === "disclosure");

const disclosureIconName = computed(() => (appendCaretExpanded.value ? "subtract-filled" : "add-filled"));

const showCheckbox = computed(() => toBoolean(props.checkbox));

const modifiers = computed(() => ({
  "--checkbox": showCheckbox.value,
  "--left-indentation": toBoolean(props.leftIndentation),
  "--right-indentation": toBoolean(props.rightIndentation),
  "--wrap": toBoolean(props.wrap),
}));
</script>

<style>
:host {
  display: block;
  content: var(--sp-comp-list-item-active-content-font);
  --list-item-default-color: var(--sp-comp-list-item-color, var(--sp-sys-color-on-surface, #000));
  --list-item-color: var(--list-item-default-color);
  --list-item-default-content-font: var(--sp-comp-list-item-content-font, var(--sp-sys-typescale-body-short));
  --list-item-content-font: var(--list-item-default-content-font);
  --list-item-background-color: var(--sp-comp-list-item-background-color, transparent);
  --list-item-hover-background-color: var(--sp-comp-list-item-hover-background-color, #eee);
  --list-item-active-background-color: var(--sp-comp-list-item-active-background-color, transparent);
  --list-item-active-color: var(--sp-comp-list-item-active-color, var(--list-item-default-color));
  --list-item-active-content-font: var(--sp-comp-list-item-active-content-font, var(--list-item-default-content-font));
  --list-item-focused-background-color: var(
    --sp-comp-list-item-focused-background-color,
    var(--list-item-hover-background-color)
  );
  /* A selected row that a theme fills darkens that fill on hover and focus, so its text stays
     readable. The `var()` inside the mix has no fallback, so the mix is invalid while no theme sets
     the fill. The two properties below then fall back to an unselected row's hover and focus
     backgrounds. */
  --list-item-active-darkened-background-color: color-mix(
    in srgb,
    var(--sp-comp-list-item-active-background-color) 92%,
    black
  );
  --list-item-active-hover-background-color: var(
    --list-item-active-darkened-background-color,
    var(--list-item-hover-background-color)
  );
  --list-item-active-focused-background-color: var(
    --list-item-active-darkened-background-color,
    var(--list-item-focused-background-color)
  );
  --list-item-highlighted-background-color: var(
    --sp-comp-list-item-highlighted-background-color,
    var(--list-item-hover-background-color)
  );
  --list-item-column-gap: var(--sp-comp-list-item-column-gap, 0.25rem);
  --list-item-min-height: var(--sp-comp-list-item-min-height, 2rem);
  --list-item-prepend-icon-fill: var(--sp-comp-list-item-prepend-icon-fill, #bbb);
  --list-item-append-icon-fill: var(--sp-comp-list-item-append-icon-fill, #bbb);
  --list-item-icon-size: var(--sp-comp-list-item-icon-size, 2rem);

  --display-divider: var(--sp-comp-list-item-display-divider, block);
  --list-item-checkbox-size: var(--sp-comp-list-item-checkbox-size, 1.25rem);
  --list-item-checkbox-border-color: var(
    --sp-comp-list-item-checkbox-border-color,
    var(--sp-sys-form-field-border-color)
  );
  --list-item-checkbox-fill: var(--sp-comp-list-item-checkbox-fill, var(--sp-sys-color-surface));
  --list-item-checkbox-check-visibility: hidden;
  --focus-outline: var(--sp-comp-list-item-focus-outline, none);
}

:host([checkbox]:hover:not([active]):not([readonly]):not([disabled])) {
  --list-item-checkbox-border-color: var(--sp-comp-list-item-checkbox-hover-border-color, var(--sp-sys-color-primary));
}

:host([readonly]) {
  --sp-comp-list-item-cursor: default;
}

:host([disabled]) {
  --sp-comp-list-item-cursor: not-allowed;
  --list-item-color: var(--sp-comp-list-item-disabled-color, var(--sp-sys-color-on-surface-variant, #9e9e9e));
  --list-item-prepend-icon-fill: var(
    --sp-comp-list-item-disabled-color,
    var(--sp-sys-color-on-surface-variant, #9e9e9e)
  );
  --list-item-append-icon-fill: var(
    --sp-comp-list-item-disabled-color,
    var(--sp-sys-color-on-surface-variant, #9e9e9e)
  );
  opacity: var(--sp-comp-list-item-disabled-opacity, 0.6);
}

:host(:hover:not([readonly]):not([disabled])),
:host(:focus:not([readonly]):not([disabled])),
:host(:focus-visible:not([readonly]):not([disabled])) {
  outline: var(--focus-outline);
  --divider-width: 100%;
  --sp-comp-divider-color: var(--list-item-background-color);
  --divider-left: 0;
}

:host(:focus:not([readonly]):not([disabled])),
:host(:focus-visible:not([readonly]):not([disabled])) {
  outline: none;
  --list-item-background-color: var(--list-item-focused-background-color);
}

/* A checkbox row shows its selection in the box, so while active it hovers like an unselected row
   unless a theme fills it. */
:host(:hover:is(:not([active]), [checkbox]):not([readonly]):not([disabled])) {
  --list-item-append-icon-fill: var(--sp-comp-list-item-prepend-icon-fill-hover, --list-item-append-icon-fill);
  --list-item-background-color: var(--list-item-hover-background-color);
}

/* The combobox pending-choice hint (see the `highlighted` prop): same visual
   treatment as hover — filled background, own divider blended in — without
   real focus or hover on the row. */
:host([highlighted]:is(:not([active]), [checkbox]):not([readonly]):not([disabled])) {
  --divider-width: 100%;
  --sp-comp-divider-color: var(--list-item-background-color);
  --divider-left: 0;
  --list-item-background-color: var(--list-item-highlighted-background-color);
}

:host([active]:not([readonly]):not([disabled])) {
  --list-item-color: var(--list-item-active-color);
  --list-item-prepend-icon-fill: var(--sp-comp-list-item-active-icon-fill, var(--sp-sys-color-success, green));
  --list-item-content-font: var(--list-item-active-content-font);
  --list-item-background-color: var(--list-item-active-background-color);
  --display-divider: var(--sp-comp-list-item-active-display-divider, var(--sp-comp-list-item-display-divider, block));
}

:host([active]:hover:not([readonly]):not([disabled])) {
  --list-item-background-color: var(--list-item-active-hover-background-color);
  --display-divider: var(
    --sp-comp-list-item-active-hover-display-divider,
    var(--sp-comp-list-item-display-divider, block)
  );
}

/* A checkbox row shows its selection in the box, so the row itself keeps the plain look unless a
   theme sets the checkbox-row tokens. */
:host([checkbox][active]:not([readonly]):not([disabled])) {
  --list-item-checkbox-border-color: var(--sp-comp-list-item-checkbox-active-border-color, var(--sp-sys-color-primary));
  --list-item-checkbox-fill: var(--sp-comp-list-item-checkbox-active-fill, var(--sp-sys-color-primary));
  --list-item-checkbox-check-visibility: visible;
  --list-item-active-background-color: var(
    --sp-comp-list-item-checkbox-row-active-background-color,
    var(--sp-comp-list-item-background-color, transparent)
  );
  --list-item-active-darkened-background-color: color-mix(
    in srgb,
    var(--sp-comp-list-item-checkbox-row-active-background-color) 92%,
    black
  );
  --list-item-active-color: var(--sp-comp-list-item-checkbox-row-active-color, var(--list-item-default-color));
  --list-item-active-content-font: var(
    --sp-comp-list-item-checkbox-row-active-content-font,
    var(--sp-sys-typescale-body-semi-bold-short)
  );
  --display-divider: var(
    --sp-comp-list-item-checkbox-row-active-display-divider,
    var(--sp-comp-list-item-display-divider, block)
  );
}

:host([active]:focus:not([readonly]):not([disabled])),
:host([active]:focus-visible:not([readonly]):not([disabled])) {
  --list-item-background-color: var(--list-item-active-focused-background-color);
}
</style>

<style scoped lang="scss">
.sp-list-item {
  --list-item-line-height: var(--sp-comp-list-item-line-height, var(--sp-sys-typescale-body-line-height, 1.25));
  --list-item-padding-inline: var(--sp-comp-list-item-padding-inline, 0.75rem);
  --list-item-padding-block: var(--sp-comp-list-item-padding-block, 0.375rem);
  --list-item-align-items: var(--sp-comp-list-item-align-items, start);
  --list-item-prepend-min-width: var(--sp-comp-list-item-prepend-min-width, 0);
  --list-item-cursor: var(--sp-comp-list-item-cursor, pointer);

  position: relative;
  display: grid;
  grid-template-columns: max-content auto max-content;
  grid-template-areas: "prepend content append";
  align-items: var(--list-item-align-items);
  padding: 0;
  padding-block: var(--list-item-padding-block);
  cursor: var(--list-item-cursor);
  transition: background-color 0.2s ease;
  min-height: var(--list-item-min-height);
  color: var(--list-item-color);

  background-color: var(--list-item-background-color);

  &.--left-indentation {
    --list-item-prepend-min-width: var(--sp-comp-list-item-prepend-min-width, 32px);
  }

  // The box sizes its own slot, so it sits one gap away from the label.
  &.--checkbox {
    --list-item-prepend-min-width: var(--list-item-checkbox-size);
    --sp-comp-list-item-prepend-margin-right: var(--sp-comp-list-item-checkbox-margin-right, var(--sp-ref-scale-12));
  }
}

.sp-list-item__text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.sp-list-item__subtitle {
  display: block;
  font-size: var(--sp-comp-list-item-subtitle-font-size, 0.875rem);
  font-weight: var(--sp-comp-list-item-subtitle-font-weight, inherit);
  line-height: var(--sp-comp-list-item-subtitle-line-height, inherit);
  color: var(--sp-comp-list-item-subtitle-color, grey);
  margin-top: var(--sp-comp-list-item-subtitle-gap, 0);
}

.sp-list-item__tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-comp-list-item-tags-gap, 0.25rem);
  margin-top: var(--sp-comp-list-item-tags-margin-top, 0.25rem);
}

.sp-list-item__tag {
  display: inline-flex;
  align-items: center;
  background-color: var(--sp-comp-list-item-tag-background-color, #eef1f2);
  color: var(--sp-comp-list-item-tag-color, inherit);
  border-radius: var(--sp-comp-list-item-tag-radius, 0.25rem);
  padding-inline: var(--sp-comp-list-item-tag-padding-inline, 0.5rem);
  padding-block: var(--sp-comp-list-item-tag-padding-block, 0.125rem);
  font-size: var(--sp-comp-list-item-tag-font-size, 0.8125rem);
  line-height: 1.4;
}

.sp-list-item__prepend {
  min-width: var(--list-item-prepend-min-width);
  grid-area: prepend;
  margin-right: var(--sp-comp-list-item-prepend-margin-right, var(--list-item-column-gap));
  margin-left: var(--sp-comp-list-item-prepend-margin-left, var(--list-item-padding-inline));

  .sp-list-item__prepend-icon {
    --fill: var(--list-item-prepend-icon-fill);
    --size: var(--list-item-icon-size);
  }

  // The box keeps the height of an icon row, so checkbox rows line up with icon rows.
  .sp-list-item__checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: var(--list-item-checkbox-size);
    height: var(--list-item-checkbox-size);
    margin-block: calc((var(--list-item-icon-size) - var(--list-item-checkbox-size)) / 2);
    border: var(--sp-comp-list-item-checkbox-border-width, 2px) solid var(--list-item-checkbox-border-color);
    border-radius: var(--sp-comp-list-item-checkbox-border-radius, var(--sp-sys-shape-control));
    background: var(--list-item-checkbox-fill);
    transition:
      background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;

    sp-icon {
      --sp-comp-icon-fill: var(--sp-comp-list-item-checkbox-check-color, var(--sp-sys-color-on-primary));
      --sp-comp-icon-size: calc(var(--list-item-checkbox-size) * 0.7);
      visibility: var(--list-item-checkbox-check-visibility);
    }
  }

  // A bare image/SVG icon (`prependIconUrl`, no decoration disc) gets its own
  // size knob so a brand glyph can be sized independently of the circle'd
  // descriptor icons in the same list. Defaults to the shared icon size, so
  // existing url-icon consumers are unaffected.
  .sp-list-item__prepend-icon--url {
    --size: var(--sp-comp-list-item-prepend-icon-url-size, var(--list-item-icon-size));
  }

  sp-avatar {
    --sp-comp-avatar-size: var(--sp-comp-list-item-prepend-avatar-size, var(--list-item-icon-size));
  }
}

// Optional decoration wrapper around the prepend icon, driven by the
// `prependIcon` descriptor's `background`. The component only maps the semantic
// `shape` enum to a radius token and applies the given background colour
// verbatim — no per-domain styling lives here; themes own the concrete values.
.sp-list-item__prepend-decoration {
  // Proportion of the glyph to the circle size — the single design knob for how
  // far the icon is inset within its background.
  --prepend-bg-icon-ratio: var(--sp-comp-list-item-prepend-bg-icon-ratio, 0.6);

  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--list-item-icon-size);
  height: var(--list-item-icon-size);

  &.--prepend-bg-circle {
    border-radius: var(--sp-comp-list-item-prepend-bg-radius-circle, 50%);
  }

  &.--prepend-bg-rounded {
    border-radius: var(--sp-comp-list-item-prepend-bg-radius-rounded, var(--sp-ref-border-radius-xs));
  }

  &.--prepend-bg-square {
    border-radius: var(--sp-comp-list-item-prepend-bg-radius-square, var(--sp-ref-border-radius-none));
  }

  // Derive the glyph size from the circle size so the inset stays proportional
  // when the circle is resized. calc() keeps it an absolute length — sp-icon
  // sizes its svg via `width/height: var(--size)`, and a raw percentage would
  // collapse because the icon's box has no definite height.
  sp-icon {
    --size: calc(var(--list-item-icon-size) * var(--prepend-bg-icon-ratio));
  }
}

.sp-list-item__content {
  --icon-size: 2rem;
  display: flex;
  grid-area: content;
  // A row stands at least an icon tall. A dense list, whose row holds one short value, sets its own.
  min-height: var(--sp-comp-list-item-content-min-height, var(--icon-size));
  padding-right: var(--list-item-column-gap);
  align-items: center;
  white-space: var(--sp-comp-list-item-content-white-space, normal);
  color: var(--list-item-color);
  font: var(--list-item-content-font);
  // Explicit, themeable line-height. The `font` shorthand above bakes in the
  // typescale's line-height (which can render too tall in dense lists); override
  // it with a dedicated token so themes can tune list density. Defaults to the
  // body typescale line-height.
  line-height: var(--list-item-line-height);

  .--wrap & {
    white-space: normal;
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

.sp-list-item__title {
  display: block;
  font-size: var(--sp-comp-list-item-title-font-size, inherit);
  font-weight: var(--sp-comp-list-item-title-font-weight, inherit);
  line-height: var(--sp-comp-list-item-title-line-height, inherit);
}

.sp-list-item__append {
  grid-area: append;
  margin-right: var(--sp-comp-list-item-append-margin-right, var(--list-item-padding-inline));
  margin-left: var(--list-item-column-gap);

  &.--loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  sp-progress {
    margin-right: var(--sp-ref-scale-16);
  }

  sp-icon {
    --fill: var(--list-item-append-icon-fill);
    --size: var(--list-item-icon-size);
  }
}

.sp-list-item__divider {
  display: var(--display-divider);
  position: absolute;
  top: 0;
  left: var(--divider-left, var(--list-item-padding-inline));
  width: var(--divider-width, calc(100% - var(--list-item-padding-inline) * 2));
}
</style>
